<template>
  <div class="font-sans min-h-screen bg-white dark:bg-gray-900">
    <!-- Packages Header with Hero Background -->
    <section
      class="relative py-12 bg-blue-50 dark:bg-gray-800 overflow-hidden border-b border-gray-200 dark:border-gray-700"
    >
      <div
        class="absolute inset-0 opacity-[0.03] pointer-events-none overflow-hidden flex flex-wrap content-start"
      >
        <svg
          v-for="i in 100"
          :key="i"
          class="w-16 h-16 text-[#293146] dark:text-gray-700 m-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="1.5"
            d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
          ></path>
        </svg>
      </div>

      <div class="max-w-screen-xl mx-auto px-4 sm:px-6 relative z-10 text-center">
        <h1 class="text-4xl md:text-5xl font-black text-[#293146] dark:text-white mb-4">
          Paket Posting Lowongan Kerja
        </h1>
        <p class="text-gray-500 dark:text-gray-300 max-w-2xl mx-auto text-lg leading-relaxed">
          Pilih paket terbaik untuk kebutuhan rekrutmen Anda
        </p>
      </div>
    </section>

    <!-- Packages Section -->
    <section id="packages" class="py-12 bg-gray-50 dark:bg-gray-900">
      <div class="max-w-6xl mx-auto px-4 sm:px-6">
        <div v-if="isLoading" class="flex justify-center py-20">
          <div class="w-10 h-10 border-4 border-[#C5B47A] border-t-transparent rounded-full animate-spin"></div>
        </div>
        <div v-else class="grid grid-cols-1 md:grid-cols-3 gap-8 items-stretch">
          <div
            v-for="paket in paketList"
            :key="paket.kode_paket"
            :class="[
              'rounded-3xl p-8 shadow-lg flex flex-col relative',
              paket.is_recommended
                ? 'bg-[#293146] dark:bg-gray-700 text-white'
                : 'bg-white dark:bg-gray-800 border-2',
            ]"
            :style="paket.is_recommended ? {} : { borderColor: paket.warna || '#D1D5DB' }"
          >
            <div
              v-if="paket.is_recommended"
              class="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#C5B47A] text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1"
            >
              <Icon v-if="paket.icon" :icon="paket.icon" class="w-3 h-3" />
              <svg v-else class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
              Rekomendasi
            </div>
            <h3
              class="text-xl font-bold mb-2"
              :class="paket.is_recommended ? 'text-white dark:text-gray-100 mt-2' : 'text-[#293146] dark:text-white'"
            >
              {{ paket.nama_paket }}
            </h3>
            <div class="flex items-baseline mb-6">
              <span
                class="text-3xl font-black"
                :class="paket.is_recommended ? 'text-white dark:text-gray-100' : 'text-[#293146] dark:text-white'"
              >
                Rp {{ formatPrice(paket.harga) }}
              </span>
            </div>
            <!-- eslint-disable vue/no-v-html -->
            <div
              class="paket-deskripsi mb-8 flex-grow"
              :class="paket.is_recommended ? 'text-white dark:text-gray-100' : 'text-gray-800 dark:text-gray-300'"
              v-html="sanitizeHtml(paket.deskripsi)"
            ></div>
            <!-- eslint-enable vue/no-v-html -->
            <button
              @click="openSelectionModal(paket.kode_paket)"
              class="w-full py-3 px-4 bg-[#C5B47A] dark:bg-yellow-400 hover:bg-[#d98d00] dark:hover:bg-yellow-500 text-[#293146] dark:text-gray-900 font-bold rounded-xl transition-all active:scale-95"
            >
              Pilih Paket
            </button>
          </div>
        </div>
      </div>
    </section>

    <!-- Why Choose Us (Below Packages) -->
    <section class="py-12 bg-gray-50/50 dark:bg-gray-800/50">
      <div class="max-w-screen-xl mx-auto px-4 sm:px-6">
        <div class="flex flex-col md:flex-row items-center gap-16">
          <div class="flex-1">
            <span
              class="text-sm font-black text-blue-600 dark:text-blue-400 uppercase tracking-[0.2em] mb-4 block"
              >Keunggulan Kami</span
            >
            <h2 class="text-4xl font-black text-[#293146] dark:text-white mb-6 leading-tight">
              Platform Rekrutmen Paling
              <span class="text-blue-600 dark:text-blue-400">Terpercaya</span>
              di Provinsi Jambi
            </h2>
            <p class="text-gray-600 dark:text-gray-300 mb-10 text-lg leading-relaxed">
              Kami membantu pertumbuhan bisnis Anda dengan ekosistem digital yang solid untuk
              menjangkau talenta terbaik.
            </p>

            <div class="space-y-6">
              <div class="flex items-start space-x-4">
                <div
                  class="mt-1 flex-shrink-0 w-12 h-12 bg-white dark:bg-gray-700 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-600 flex items-center justify-center text-blue-600 dark:text-blue-400"
                >
                  <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                    ></path>
                  </svg>
                </div>
                <div>
                  <h4 class="text-lg font-bold text-[#293146] dark:text-white mb-1">
                    Komunitas Lokal Terbesar
                  </h4>
                  <p class="text-gray-500 dark:text-gray-400">
                    Jangkau kandidat yang benar-benar berdomisili di Jambi dan sekitarnya.
                  </p>
                </div>
              </div>
              <div class="flex items-start space-x-4">
                <div
                  class="mt-1 flex-shrink-0 w-12 h-12 bg-white dark:bg-gray-700 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-600 flex items-center justify-center text-blue-600 dark:text-blue-400"
                >
                  <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z"
                    ></path>
                  </svg>
                </div>
                <div>
                  <h4 class="text-lg font-bold text-[#293146] dark:text-white mb-1">
                    Promosi Multi-Platform
                  </h4>
                  <p class="text-gray-500 dark:text-gray-400">
                    Iklan Anda otomatis ditayangkan di Website, Instagram, Telegram, dan WhatsApp.
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div class="flex-1 w-full max-w-lg hidden md:block">
            <div class="relative">
              <div class="absolute -inset-4 bg-[#C5B47A] rounded-[40px] rotate-3 -z-10"></div>
              <div class="bg-white dark:bg-gray-800 p-4 rounded-[40px] shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1542744173-8e7e53415bb0?q=80&w=800&auto=format&fit=crop"
                  alt="Business Meeting"
                  class="rounded-[32px] w-full object-cover aspect-square"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- How to Order Section -->
    <section class="py-12 bg-white dark:bg-gray-900 border-y border-gray-100 dark:border-gray-700">
      <div class="max-w-screen-xl mx-auto px-4 sm:px-6">
        <div class="text-center mb-16">
          <span
            class="text-sm font-black text-blue-600 dark:text-blue-400 uppercase tracking-[0.2em] mb-4 block"
            >Prosedur</span
          >
          <h2 class="text-4xl font-black text-[#293146] dark:text-white">Cara Pemesanan</h2>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-4 gap-12 text-center relative">
          <!-- Connection lines (desktop) -->
          <div
            class="hidden md:block absolute top-10 left-[15%] right-[15%] h-0.5 bg-blue-100 dark:bg-gray-700"
          ></div>

          <div class="relative group">
            <div
              class="w-20 h-20 bg-white dark:bg-gray-800 border-2 border-blue-50 dark:border-gray-700 text-[#293146] dark:text-white rounded-[24px] flex items-center justify-center text-3xl font-black mx-auto mb-6 shadow-sm group-hover:bg-[#293146] dark:group-hover:bg-[#C5B47A] group-hover:text-white dark:group-hover:text-gray-900 group-hover:scale-110 transition-all duration-300 relative z-10"
            >
              1
            </div>
            <h4 class="text-xl font-black text-[#293146] dark:text-white mb-3">Pilih Paket</h4>
            <p class="text-gray-500 dark:text-gray-400 text-sm leading-relaxed">
              Tentukan paket iklan yang paling sesuai dengan kebutuhan rekrutmen Anda.
            </p>
          </div>
          <div class="relative group">
            <div
              class="w-20 h-20 bg-white dark:bg-gray-800 border-2 border-blue-50 dark:border-gray-700 text-[#293146] dark:text-white rounded-[24px] flex items-center justify-center text-3xl font-black mx-auto mb-6 shadow-sm group-hover:bg-[#293146] dark:group-hover:bg-[#C5B47A] group-hover:text-white dark:group-hover:text-gray-900 group-hover:scale-110 transition-all duration-300 relative z-10"
            >
              2
            </div>
            <h4 class="text-xl font-black text-[#293146] dark:text-white mb-3">Isi Detail</h4>
            <p class="text-gray-500 dark:text-gray-400 text-sm leading-relaxed">
              Lengkapi informasi lowongan pekerjaan secara mendetail melalui form kami.
            </p>
          </div>
          <div class="relative group">
            <div
              class="w-20 h-20 bg-white dark:bg-gray-800 border-2 border-blue-50 dark:border-gray-700 text-[#293146] dark:text-white rounded-[24px] flex items-center justify-center text-3xl font-black mx-auto mb-6 shadow-sm group-hover:bg-[#293146] dark:group-hover:bg-[#C5B47A] group-hover:text-white dark:group-hover:text-gray-900 group-hover:scale-110 transition-all duration-300 relative z-10"
            >
              3
            </div>
            <h4 class="text-xl font-black text-[#293146] dark:text-white mb-3">Konfirmasi</h4>
            <p class="text-gray-500 dark:text-gray-400 text-sm leading-relaxed">
              Lakukan pembayaran (untuk paket berbayar) dan tim kami akan memproses segera.
            </p>
          </div>
          <div class="relative group">
            <div
              class="w-20 h-20 bg-[#C5B47A] dark:bg-yellow-500 border-2 border-yellow-300 dark:border-yellow-600 text-[#293146] rounded-[24px] flex items-center justify-center text-3xl font-black mx-auto mb-6 shadow-xl group-hover:scale-110 transition-all duration-300 relative z-10"
            >
              4
            </div>
            <h4 class="text-xl font-black text-[#293146] dark:text-white mb-3">Iklan Tayang</h4>
            <p class="text-gray-500 dark:text-gray-400 text-sm leading-relaxed">
              Lowongan Anda akan tayang di semua platform yang dipilih maksimal dalam 24 jam.
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- Tips Section -->
    <section class="py-12 bg-blue-50/30 dark:bg-gray-800/30">
      <div class="max-w-screen-xl mx-auto px-4 sm:px-6">
        <div
          class="bg-white dark:bg-gray-800 rounded-[50px] p-10 md:p-16 border border-gray-100 dark:border-gray-700 shadow-xl overflow-hidden relative"
        >
          <div
            class="absolute top-0 right-0 w-64 h-64 bg-[#C5B47A]/5 rounded-full -mr-32 -mt-32"
          ></div>

          <div class="flex flex-col lg:flex-row gap-16 items-center relative z-10">
            <div class="lg:w-1/2">
              <span
                class="text-sm font-black text-blue-600 dark:text-blue-400 uppercase tracking-[0.2em] mb-4 block"
                >Tips Sukses</span
              >
              <h2 class="text-4xl font-black text-[#293146] dark:text-white mb-6">
                Cara Membuat Lowongan yang
                <span class="text-blue-600 dark:text-blue-400">Menarik</span>
              </h2>
              <p class="text-gray-500 dark:text-gray-300 mb-8 leading-relaxed text-lg">
                Lowongan yang detail dan profesional akan menarik kandidat yang lebih berkualitas.
                Berikut beberapa tips dari tim kami:
              </p>

              <div class="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div class="flex items-start space-x-3">
                  <div
                    class="w-6 h-6 bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-300 rounded-full flex items-center justify-center flex-shrink-0 mt-1"
                  >
                    <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fill-rule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clip-rule="evenodd"
                      ></path>
                    </svg>
                  </div>
                  <span class="text-sm font-bold text-gray-700 dark:text-gray-200"
                    >Gunakan Judul Posisi yang Jelas</span
                  >
                </div>
                <div class="flex items-start space-x-3">
                  <div
                    class="w-6 h-6 bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-300 rounded-full flex items-center justify-center flex-shrink-0 mt-1"
                  >
                    <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fill-rule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clip-rule="evenodd"
                      ></path>
                    </svg>
                  </div>
                  <span class="text-sm font-bold text-gray-700 dark:text-gray-200"
                    >Sebutkan Range Gaji (Opsional)</span
                  >
                </div>
              </div>
            </div>
            <div class="lg:w-1/2 w-full">
              <div
                class="bg-gray-50 dark:bg-gray-700 rounded-3xl p-8 border border-gray-100 dark:border-gray-600 italic text-gray-600 dark:text-gray-300 relative"
              >
                <svg
                  class="absolute -top-4 -left-4 w-10 h-10 text-blue-200 dark:text-blue-400"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    d="M14.017 21L14.017 18C14.017 16.8954 14.9124 16 16.017 16H19.017C19.5693 16 20.017 15.5523 20.017 15V9C20.017 8.44772 19.5693 8 19.017 8H15.017C14.4647 8 14.017 8.44772 14.017 9V12C14.017 12.5523 13.5693 13 13.017 13H10.017V21H14.017ZM10.017 21L10.017 18C10.017 16.8954 9.12157 16 8.01699 16H5.01699C4.46471 16 4.01699 15.5523 4.01699 15V9C4.01699 8.44772 4.46471 8 5.01699 8H9.01699C9.56927 8 10.017 8.44772 10.017 9V12C10.017 12.5523 10.4647 13 11.017 13H14.017V21H10.017Z"
                  />
                </svg>
                "Lowongan yang menyertakan informasi gaji cenderung mendapatkan pelamar 3x lebih
                banyak dan berkualitas."
                <p class="mt-4 not-italic font-black text-[#293146] dark:text-white text-sm">
                  — Tim Recruitment lokerjambi.co.id
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- FAQ Section -->
    <section class="py-12 bg-white dark:bg-gray-900">
      <div class="max-w-3xl mx-auto px-4 sm:px-6">
        <div class="text-center mb-16">
          <span
            class="text-sm font-black text-blue-600 dark:text-blue-400 uppercase tracking-[0.2em] mb-4 block"
            >Bantuan</span
          >
          <h2 class="text-4xl font-black text-[#293146] dark:text-white">
            Pertanyaan Sering Diajukan
          </h2>
        </div>

        <div class="space-y-6">
          <div
            v-for="(faq, index) in faqs"
            :key="index"
            class="border-2 border-gray-200 dark:border-gray-700 rounded-[24px] md:rounded-[32px] overflow-hidden shadow-md hover:shadow-lg transition-shadow"
          >
            <button
              @click="toggleFaq(index)"
              class="w-full flex items-center justify-between p-6 md:p-8 text-left hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              <span class="text-lg font-bold text-[#293146] dark:text-white">{{
                faq.question
              }}</span>
              <svg
                class="w-5 h-5 text-gray-400 dark:text-gray-500 transition-transform duration-300"
                :class="{ 'rotate-180': activeFaq === index }"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M19 9l-7 7-7-7"
                ></path>
              </svg>
            </button>
            <div
              v-show="activeFaq === index"
              class="px-8 pb-8 text-gray-500 dark:text-gray-300 leading-relaxed animate-fadeIn"
              v-sanitize="faq.answer"
            ></div>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA Final Section -->
    <section class="py-12">
      <div class="max-w-screen-xl mx-auto px-4 sm:px-6">
        <div
          class="bg-[#293146] dark:bg-gray-800 rounded-[50px] p-12 md:p-24 text-center relative overflow-hidden"
        >
          <div class="absolute inset-0 opacity-10 pointer-events-none">
            <svg
              class="w-full h-full"
              fill="currentColor"
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
            >
              <path d="M0,0 L100,0 L100,100 L0,100 Z" fill="url(#grid-pattern)" />
              <defs>
                <pattern id="grid-pattern" width="10" height="10" patternUnits="userSpaceOnUse">
                  <path d="M 10 0 L 0 0 0 10" fill="none" stroke="white" stroke-width="0.5" />
                </pattern>
              </defs>
            </svg>
          </div>
          <div class="relative z-10">
            <h2 class="text-4xl md:text-5xl font-black text-white dark:text-white mb-8">
              Siap Menemukan Karyawan Terbaik?
            </h2>
            <p
              class="text-blue-200 dark:text-gray-300 text-lg mb-12 max-w-2xl mx-auto leading-relaxed"
            >
              Jangan tunda pertumbuhan bisnis Anda. Pasang iklan lowongan sekarang dan rasakan
              kemudahannya.
            </p>
            <div
              class="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4"
            >
              <a
                href="#packages"
                class="px-10 py-5 bg-[#C5B47A] dark:bg-yellow-500 hover:bg-[#d98d00] dark:hover:bg-yellow-600 text-[#293146] dark:text-gray-900 font-black rounded-2xl shadow-xl transition-all hover:scale-105 active:scale-95"
                >Mulai Sekarang</a
              >
              <button
                class="px-10 py-5 bg-white/10 dark:bg-gray-700/50 hover:bg-white/20 dark:hover:bg-gray-700 text-white dark:text-gray-200 font-black rounded-2xl border border-white/20 dark:border-gray-600 transition-all"
              >
                Konsultasi Gratis
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Selection Modal (WhatsApp vs Form) -->
    <div
      v-if="showSelectionModal"
      class="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 dark:bg-black/80 backdrop-blur-md animate-fadeIn"
    >
      <div
        class="bg-white dark:bg-gray-800 rounded-[40px] p-8 md:p-12 max-w-xl w-full shadow-2xl animate-scaleUp relative overflow-hidden"
      >
        <div
          class="absolute top-0 right-0 w-32 h-32 bg-[#C5B47A]/10 rounded-full -mr-16 -mt-16"
        ></div>

        <button
          @click="showSelectionModal = false"
          class="absolute top-8 right-8 text-gray-400 dark:text-gray-500 hover:text-gray-900 dark:hover:text-white transition-colors z-20"
        >
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="3"
              d="M6 18L18 6M6 6l12 12"
            ></path>
          </svg>
        </button>

        <div class="text-center mb-10 relative z-10">
          <span
            class="inline-block px-5 py-2 mb-6 text-[10px] font-black tracking-widest text-[#293146] uppercase bg-[#C5B47A] rounded-full"
            >Konfirmasi Pilihan</span
          >
          <h3 class="text-3xl font-black text-[#293146] dark:text-white mb-2">
            Paket {{ selectedPackage?.nama_paket }}
          </h3>
          <p class="text-gray-500 dark:text-gray-300">Pilih kenyamanan cara pemesanan Anda</p>
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 relative z-10">
          <!-- Option WhatsApp -->
          <button
            @click="settings.order_method_whatsapp_enabled ? selectMethod('whatsapp') : null"
            :disabled="!settings.order_method_whatsapp_enabled"
            class="group p-8 border-2 border-gray-100 dark:border-gray-700 rounded-[32px] transition-all duration-300 text-left flex flex-col items-center relative overflow-hidden"
            :class="{
              'hover:border-green-500 dark:hover:border-green-400 hover:bg-green-50 dark:hover:bg-green-900/50':
                settings.order_method_whatsapp_enabled,
              'opacity-60 grayscale cursor-not-allowed bg-gray-50 dark:bg-gray-900':
                !settings.order_method_whatsapp_enabled,
            }"
          >
            <!-- Tooltip overlay for disabled state -->
            <div
              v-if="!settings.order_method_whatsapp_enabled"
              class="absolute inset-0 flex items-center justify-center bg-white/10 dark:bg-black/10 backdrop-blur-[1px] z-10"
            >
              <span
                class="bg-[#293146] text-white text-[10px] px-3 py-1.5 rounded-full font-black uppercase tracking-widest shadow-2xl transform -rotate-12 border-2 border-[#C5B47A]"
                >Coming Soon</span
              >
            </div>

            <div
              class="w-16 h-16 bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-300 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-500"
            >
              <svg class="w-9 h-9" fill="currentColor" viewBox="0 0 24 24">
                <path
                  d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.246 2.248 3.484 5.232 3.483 8.413-.003 6.557-5.338 11.892-11.893 11.892-1.997-.001-3.951-.5-5.688-1.448l-6.308 1.654zm6.233-4.087l.446.265c1.512.9 3.303 1.374 5.14 1.375 5.565 0 10.088-4.523 10.091-10.088.002-2.697-1.047-5.232-2.956-7.141-1.909-1.908-4.44-2.956-7.142-2.957-5.564 0-10.088 4.523-10.091 10.088-.001 1.89.504 3.734 1.462 5.334l.32.533-1.056 3.86 3.951-1.036zm10.654-7.422c-.22-.11-1.3-.642-1.49-.113-.19-.072-.328-.11-.478-.328-.15-.22-.58-1.203-.71-1.472-.13-.27-.27-.27-.478-.28-.11 0-.328-.022-.529-.022s-.528.077-.805.373c-.276.299-1.056 1.033-1.056 2.517s1.08 2.914 1.23 3.111c.15.197 2.126 3.246 5.15 4.554.72.311 1.282.497 1.72.637.722.222 1.38.191 1.9.113.58-.087 1.3-.531 1.485-1.043.186-.511.186-.949.13-1.043-.056-.094-.206-.15-.426-.26z"
                />
              </svg>
            </div>
            <span class="text-xl font-black text-gray-900 dark:text-white mb-1">WhatsApp</span>
            <span
              class="text-xs text-gray-500 dark:text-gray-400 font-bold uppercase tracking-widest"
              >Respon Instan</span
            >
          </button>

          <!-- Option Form -->
          <button
            @click="settings.order_method_form_enabled ? selectMethod('web') : null"
            :disabled="!settings.order_method_form_enabled"
            class="group p-8 border-2 border-gray-100 dark:border-gray-700 rounded-[32px] transition-all duration-300 text-left flex flex-col items-center relative overflow-hidden"
            :class="{
              'hover:border-blue-600 dark:hover:border-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/50':
                settings.order_method_form_enabled,
              'opacity-60 grayscale cursor-not-allowed bg-gray-50 dark:bg-gray-900':
                !settings.order_method_form_enabled,
            }"
          >
            <!-- Tooltip overlay for disabled state -->
            <div
              v-if="!settings.order_method_form_enabled"
              class="absolute inset-0 flex items-center justify-center bg-white/10 dark:bg-black/10 backdrop-blur-[1px] z-10"
            >
              <span
                class="bg-[#293146] text-white text-[10px] px-3 py-1.5 rounded-full font-black uppercase tracking-widest shadow-2xl transform rotate-12 border-2 border-[#C5B47A]"
                >Coming Soon</span
              >
            </div>

            <div
              class="w-16 h-16 bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-300 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-500"
            >
              <svg class="w-9 h-9" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                ></path>
              </svg>
            </div>
            <span class="text-xl font-black text-gray-900 dark:text-white mb-1 text-nowrap"
              >Formulir Web</span
            >
            <span
              class="text-xs text-gray-500 dark:text-gray-400 font-bold uppercase tracking-widest"
              >Input Mandiri</span
            >
          </button>
        </div>

        <p
          class="text-center text-[10px] text-gray-400 dark:text-gray-500 mt-12 uppercase tracking-[0.3em] font-black border-t border-gray-100 dark:border-gray-700 pt-6 relative z-10"
        >
          Layanan Bisnis Profesional lokerjambi.co.id
        </p>
      </div>
    </div>

    <!-- Footer Space -->
    <AppFooter />
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useFaq } from '../composables/useFaq';
import { useContact } from '../composables/useContact';
import { usePaketLayanan } from '../composables/usePaketLayanan';
import AppFooter from '../components/layout/AppFooter.vue';
import axios from 'axios';
import { event as gtagEvent } from 'vue-gtag';
import { useSeoMeta } from '../composables/useSeoMeta';
import { Icon } from '@iconify/vue';
import { sanitizeHtml } from '@/utils/sanitize';

useSeoMeta({
  title: 'Pasang Iklan Lowongan Kerja di Jambi',
  description:
    'Pasang iklan lowongan kerja di Lokerjambi.co.id. Tersedia berbagai paket pilihan untuk menjangkau ribuan pencari kerja di Jambi.',
  path: '/post-job',
  type: 'website',
});

const router = useRouter();
const showSelectionModal = ref(false);
const selectedPackage = ref(null);
const { faqs, activeFaq, fetchFaqs, toggleFaq } = useFaq();
const { contact, fetchContact } = useContact();
const { paketList, isLoading, fetchPaketLayanan, getPaketByKode } = usePaketLayanan();

const settings = ref({
  order_method_whatsapp_enabled: true,
  order_method_form_enabled: true,
});

const fetchSettings = async () => {
  try {
    const response = await axios.get('/frontend/settings');
    if (response.data) {
      if (response.data.order_method_whatsapp_enabled !== undefined) {
        settings.value.order_method_whatsapp_enabled =
          response.data.order_method_whatsapp_enabled === 'true' ||
          response.data.order_method_whatsapp_enabled === true;
      }
      if (response.data.order_method_form_enabled !== undefined) {
        settings.value.order_method_form_enabled =
          response.data.order_method_form_enabled === 'true' ||
          response.data.order_method_form_enabled === true;
      }
    }
  } catch {
    // Failed to fetch settings
  }
};

onMounted(() => {
  fetchFaqs();
  fetchSettings();
  fetchContact();
  fetchPaketLayanan();
});

// WhatsApp Configuration
const waNumber = computed(() => {
  return contact.value?.admin?.phone
    ? contact.value.admin.phone.replace(/[^0-9]/g, '')
    : '6282226968529';
});
const waBaseUrl = computed(() => `https://wa.me/${waNumber.value}`);

const formatPrice = (price) => {
  return new Intl.NumberFormat('id-ID').format(price);
};

const openSelectionModal = (kodePaket) => {
  selectedPackage.value = getPaketByKode(kodePaket);
  showSelectionModal.value = true;
};

const selectMethod = (method) => {
  showSelectionModal.value = false;

  if (!selectedPackage.value) return;

  gtagEvent('select_package', {
    package_name: selectedPackage.value.nama_paket,
    price: selectedPackage.value.harga,
    currency: 'IDR',
    order_method: method,
  });

  if (method === 'whatsapp') {
    const message = `Halo Admin lokerjambi.co.id, saya tertarik untuk pasang iklan lowongan dengan *Paket ${selectedPackage.value.nama_paket}*. Mohon informasinya lebih lanjut.`;
    window.open(`${waBaseUrl.value}?text=${encodeURIComponent(message)}`, '_blank');
  } else {
    router.push({
      name: 'post-job-form',
      query: { package: selectedPackage.value.kode_paket },
    });
  }
};
</script>

<style scoped>
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes scaleUp {
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.animate-fadeIn {
  animation: fadeIn 0.4s ease-out forwards;
}

.animate-scaleUp {
  animation: scaleUp 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
}

html {
  scroll-behavior: smooth;
}

.paket-deskripsi :deep(ul) {
  list-style: disc;
  list-style-position: outside;
  margin-left: 1.25rem;
}

.paket-deskripsi :deep(ul li) {
  margin-bottom: 0.5rem;
}

.paket-deskripsi :deep(ol) {
  list-style: decimal;
  list-style-position: outside;
  margin-left: 1.25rem;
}

.paket-deskripsi :deep(ol li) {
  margin-bottom: 0.5rem;
}

.paket-deskripsi :deep(p) {
  margin-bottom: 0.5rem;
}

.paket-deskripsi :deep(a) {
  text-decoration: underline;
}

.paket-deskripsi :deep(h1),
.paket-deskripsi :deep(h2),
.paket-deskripsi :deep(h3) {
  font-weight: 700;
  margin-bottom: 0.5rem;
}

.paket-deskripsi :deep(s) {
  text-decoration: line-through;
  text-decoration-thickness: 2px;
}

.paket-deskripsi :deep(.badge) {
  display: inline-flex;
  align-items: center;
  font-size: 10px;
  font-weight: 700;
  padding: 0.125rem 0.5rem;
  border-radius: 9999px;
  vertical-align: middle;
  margin: 0 0.125rem;
}

.paket-deskripsi :deep(.badge-discount) {
  color: #dc2626;
  background-color: #fef2f2;
}

.paket-deskripsi :deep(.badge-promo) {
  color: #2563eb;
  background-color: #eff6ff;
}

.paket-deskripsi :deep(.badge-new) {
  color: #16a34a;
  background-color: #f0fdf4;
}

.paket-deskripsi :deep(.badge-sale) {
  color: #ea580c;
  background-color: #fff7ed;
}
</style>
